import React, {useMemo, useRef, useEffect} from 'react';
import styled from 'styled-components';
import type {MentionUser} from '../hooks/useMentionUsers';

interface Props {
	users: MentionUser[];
	query: string;
	activeIndex: number;
	onSelect: (user: MentionUser) => void;
	position: {top: number; left: number};
}

const Popover = styled.div`
	position: absolute;
	z-index: 50;
	width: 256px;
	overflow: hidden;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
`;

const EmptyState = styled.p`
	margin: 0;
	padding: 8px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #94a3b8;
`;

const OptionList = styled.ul`
	margin: 0;
	max-height: 180px;
	overflow-y: auto;
	padding: 4px 0;
	list-style: none;
`;

const Option = styled.li<{$active: boolean}>`
	display: flex;
	cursor: pointer;
	align-items: center;
	gap: 8px;
	padding: 6px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	background: ${p => (p.$active ? '#eff6ff' : 'transparent')};
	color: ${p => (p.$active ? '#1e3a8a' : '#1e293b')};
	&:hover {
		background: ${p => (p.$active ? '#eff6ff' : '#f8fafc')};
	}
`;

const Avatar = styled.span`
	display: inline-flex;
	height: 24px;
	width: 24px;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	border-radius: ${p => p.theme.ticketing.radius.full};
	background: #e0e7ff;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: #4338ca;
`;

const UserInfo = styled.span`
	display: flex;
	flex-direction: column;
	overflow: hidden;
`;

const UserName = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-weight: 500;
`;

const UserEmail = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font: ${p => p.theme.ticketing.font.body3};
	color: #64748b;
`;

export function MentionSuggestions({users, query, activeIndex, onSelect, position}: Props) {
	const listRef = useRef<HTMLUListElement>(null);

	const filtered = useMemo(() => {
		if (!query.trim()) return users;
		const lower = query.toLowerCase();
		return users.filter(u => u.name.toLowerCase().includes(lower) || u.email.toLowerCase().includes(lower));
	}, [users, query]);

	useEffect(() => {
		if (!listRef.current) return;
		const activeItem = listRef.current.children[activeIndex] as HTMLElement | undefined;
		activeItem?.scrollIntoView?.({block: 'nearest'});
	}, [activeIndex]);

	return (
		<Popover style={{top: position.top, left: position.left}}>
			{filtered.length === 0 ? (
				<EmptyState>No matches</EmptyState>
			) : (
				<OptionList ref={listRef} role="listbox">
					{filtered.map((user, idx) => (
						<Option
							key={user.id}
							role="option"
							aria-selected={idx === activeIndex}
							$active={idx === activeIndex}
							onMouseDown={e => {
								e.preventDefault();
								onSelect(user);
							}}
						>
							<Avatar>
								{user.name
									.split(/\s+/)
									.map(p => p[0])
									.join('')
									.slice(0, 2)
									.toUpperCase()}
							</Avatar>
							<UserInfo>
								<UserName>{user.name}</UserName>
								<UserEmail>{user.email}</UserEmail>
							</UserInfo>
						</Option>
					))}
				</OptionList>
			)}
		</Popover>
	);
}
